// Shared helpers for admin features: roles, the list of known names, people, invitations and renaming
// The security rules in firestore.rules enforce the same roles server side

import { useCallback, useEffect, useState } from 'react';
import {
  getFirestore, collection, doc, getDoc, getDocs, setDoc, updateDoc, addDoc, query, where, serverTimestamp,
} from 'firebase/firestore';
import { app } from './firebase-config';

const db = getFirestore(app);

// Keep in sync with isSuperAdmin() in firestore.rules
export const SUPER_ADMIN_UIDS = [
  'QGTFJzn6o2dOljNW5vlGfVhZQzw1', // Njaal
  'PAA7WTgXPvfoEWhHFNtujwuxL4p1', // Matias (matias.klingsholm@gmail.com)
];

export const EVENT_TYPES = ['KL🥩', 'Vino🍷', 'Blåtur🌍', 'KD🧠', 'Julegrøt🎅', 'Fitness💪', 'Julebord🎄'];

export const normalizeName = (name) => (name || '').trim().toLowerCase();

// Roles for the signed-in user, based on config/blatur
export function useRoles(user) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) {
      setConfig(null);
      setLoading(user === undefined);
      return;
    }
    try {
      const snap = await getDoc(doc(db, 'config', 'blatur'));
      setConfig(snap.exists() ? snap.data() : null);
    } catch (error) {
      console.error('Error fetching blåtur config: ', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const isSuperAdmin = !!user && SUPER_ADMIN_UIDS.includes(user.uid);
  const isOrganizer = !!user && !!config?.organizers?.includes(user.uid);
  return { config, loading, isSuperAdmin, isOrganizer, isBlaturAdmin: isSuperAdmin || isOrganizer, reload };
}

// Everyone the app knows: accounts (users) and people without an account (people)
export async function loadDirectory() {
  const [usersSnap, peopleSnap] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'people')).catch(() => null), // not readable until the new rules are deployed
  ]);
  const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => (u.name || '').trim());
  const people = peopleSnap ? peopleSnap.docs.map(d => ({ id: d.id, ...d.data() })) : [];
  const names = new Map();
  users.forEach(u => names.set(normalizeName(u.name), u.name.trim()));
  people.forEach(p => { if (!names.has(normalizeName(p.name))) names.set(normalizeName(p.name), p.name); });
  return { users, people, names: [...names.values()].sort((a, b) => a.localeCompare(b, 'no')) };
}

export async function createPerson({ name, fullName, email }, createdBy) {
  const ref = await addDoc(collection(db, 'people'), {
    name: name.trim(),
    fullName: (fullName || '').trim(),
    email: (email || '').trim(),
    uid: null,
    createdBy,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

const randomToken = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

// Creates an invitation link for a person without an account
export async function createInvite(person, createdBy) {
  const token = randomToken();
  await setDoc(doc(db, 'invites', token), {
    personId: person.id,
    name: person.name,
    usedBy: null,
    createdBy,
    createdAt: serverTimestamp(),
  });
  return `${window.location.origin}/invitasjon/${token}`;
}

// Renames a person everywhere: their account, the people register, the blåtur invitation list
// and every event's hosts and participants
export async function renamePerson(oldName, newName) {
  const from = oldName;
  const to = newName.trim();
  let changedEvents = 0;

  const [usersSnap, peopleSnap, membersSnap, aspirantsSnap, eventsSnap] = await Promise.all([
    getDocs(query(collection(db, 'users'), where('name', '==', from))),
    getDocs(query(collection(db, 'people'), where('name', '==', from))),
    getDocs(query(collection(db, 'blatur_members'), where('name', '==', from))),
    getDocs(query(collection(db, 'blatur_aspirants'), where('name', '==', from))),
    getDocs(collection(db, 'events')),
  ]);

  for (const d of [...usersSnap.docs, ...peopleSnap.docs, ...membersSnap.docs, ...aspirantsSnap.docs]) {
    await updateDoc(d.ref, { name: to });
  }

  const replace = (list) => {
    const result = (list || []).map(n => (n === from ? to : n));
    return result.filter((n, i) => result.indexOf(n) === i); // avoid duplicates if both names were present
  };

  for (const d of eventsSnap.docs) {
    const data = d.data();
    if ((data.Deltagelse || []).includes(from) || (data.Vert || []).includes(from)) {
      await updateDoc(d.ref, { Deltagelse: replace(data.Deltagelse), Vert: replace(data.Vert) });
      changedEvents += 1;
    }
  }

  return { accounts: usersSnap.size, people: peopleSnap.size, events: changedEvents };
}

// Downloads all readable collections as a JSON file (a simple backup that needs no console access)
export async function downloadBackup() {
  const backup = { exportedAt: new Date().toISOString(), collections: {} };
  for (const name of ['events', 'users', 'people', 'invites', 'blatur_members', 'blatur_applications', 'blatur_aspirants', 'emails']) {
    try {
      const snap = await getDocs(collection(db, name));
      backup.collections[name] = Object.fromEntries(snap.docs.map(d => [d.id, d.data()]));
    } catch (error) {
      backup.collections[name] = { error: error.message }; // e.g. not readable with the current rules
    }
  }
  try {
    const config = await getDoc(doc(db, 'config', 'blatur'));
    backup.collections.config = config.exists() ? { blatur: config.data() } : {};
  } catch (error) {
    backup.collections.config = { error: error.message };
  }
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `laughub-backup-${backup.exportedAt.slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
  return Object.fromEntries(Object.entries(backup.collections).map(([k, v]) => [k, v.error ? 'ikke tilgang' : Object.keys(v).length]));
}
